// 🔧 my_orders.js
// ✅ Purpose: Public-facing buyer order history page
// ✅ Features:
// - Shows the logged-in user's event orders from /events/{eventId}/orders
// - Displays ticket purchases, payment status, quantity, amount, and order date
// - Links users back to Buy Tickets
// - Prepares shared buyer history for future raffle, auction, and swag orders
// - Read-only page; payment status is controlled by Stripe webhook
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { Link, useLocation } from "react-router-dom";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../../auth_context";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";

const MyOrders = () => {

  const { user } = useAuth();
  const location = useLocation();
  const { eventId, eventslug, eventDoc, ready } = useEventContext();

  const ticketsEnabled = hasPurchasedAddOn(
    eventDoc,
    "tickets"
  );
  const auctionEnabled = hasPurchasedAddOn(eventDoc, "auction");
  const auctionOrdersOnly = auctionEnabled &&
    (!ticketsEnabled || new URLSearchParams(location.search).get("type") === "auction");
  const ordersEnabled = ticketsEnabled || auctionEnabled;
  const [orders, setOrders] = useState([]);
  const [ticketsById, setTicketsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payingOrderId, setPayingOrderId] = useState("");
  const payAuctionOrder = async (order) => {
    setPayingOrderId(order.id);
    try {
      const result = await httpsCallable(functions, "createAuctionCheckoutSession")({ eventId, auctionItemId: order.auctionItemId, auctionAllocationId: order.auctionAllocationId || "" });
      const url = new URL(result.data.url);
      if (url.protocol !== "https:" || url.hostname !== "checkout.stripe.com") throw new Error("Secure payment link is unavailable.");
      window.location.assign(url.href);
    } catch (err) {
      setError(err.message || "Unable to start auction payment. Please try again.");
      setTimeout(() => setError(""), 20000);
    } finally { setPayingOrderId(""); }
  };

  const formatMoney = (amount = 0) =>
    `$${(Number(amount || 0) / 100).toFixed(2)}`;

  const formatDate = (value) => {
    const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;

    if (!date || Number.isNaN(date.getTime())) {
      return "Not available";
    }

    return date.toLocaleString();
  };

    // =====================================================
    // 📥 Load current user's orders
    // =====================================================
    useEffect(() => {
    const loadMyOrders = async () => {
        // ✅ Wait for event context first
        if (!ready) return;

        // ✅ Prevent endless loading if user/event is not ready
        if (!eventId || !user?.uid || !ordersEnabled) {
        setOrders([]);
        setLoading(false);
        return;
        }

        try {
        setLoading(true);
        setError("");

        const ticketsSnap = ticketsEnabled && !auctionOrdersOnly
          ? await getDocs(collection(db, "events", eventId, "tickets"))
          : { docs: [] };

        const ticketMap = {};

        ticketsSnap.docs.forEach((ticketDoc) => {
            ticketMap[ticketDoc.id] = {
            id: ticketDoc.id,
            ...ticketDoc.data(),
            };
        });

        setTicketsById(ticketMap);

        const ordersQuery = query(
            collection(db, "events", eventId, "orders"),
            where("uid", "==", user.uid),
            // Rules require both owner and order type for Auction buyer reads.
            ...(auctionOrdersOnly
              ? [where("orderType", "==", "auction")]
              : [orderBy("createdAt", "desc")])
        );

        const ordersSnap = await getDocs(ordersQuery);

        setOrders(
            ordersSnap.docs.map((orderDoc) => ({
            id: orderDoc.id,
            ...orderDoc.data(),
            })).sort((left, right) =>
              (right.createdAt?.toMillis?.() || 0) - (left.createdAt?.toMillis?.() || 0)
            )
        );
        } catch (err) {
        logger.error("❌ Failed to load my orders:", {
            value: err,
        });

        setError("Failed to load your orders.");
        } finally {
        setLoading(false);
        }
    };

    loadMyOrders();
        }, [ready, eventId, user?.uid, ticketsEnabled, ordersEnabled, auctionOrdersOnly]);

    const myOrdersSeo = {
      title: `My Orders | ${eventDoc?.name || "Event"} | Let's Go Vote`,
      description:
        "View your ticket purchases and event order history.",
      path: `/${eventslug}/my_orders`,
      noindex: true,
    };

    if (!eventslug) {
    return (
        <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
            <div className="alert alert-warning">
            My Orders is event-specific. Please open this page from an event.
            </div>
        </div>
        </section>
    );
    }

    if (!ready) {
    return <FullPageSpinner message="Loading event..." />;
    }

    if (
    ready &&
    eventDoc &&
    !ordersEnabled
    ) {
    return (
        <AddonRequired
        eventslug={eventslug}
        title="Event Orders Unavailable"
        description="This event does not have a purchasing feature enabled."
        />
    );
    }

    if (loading) {
    return <FullPageSpinner message="Loading your orders..." />;
    }
    
  return (
    <>
    <SeoHead {...myOrdersSeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1000px" }}>
        <div className="row mb-4">
          <div className="col">
            <h1 className="display-5">🎟️ My Orders</h1>

            <p className="lead text-muted">
              {eventDoc?.name
                ? `Your purchases for ${eventDoc.name}.`
                : "Your event purchases."}
            </p>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {ticketsEnabled && <div className="mb-4">
          <Link
            to={`/${eventslug}/buy_tickets`}
            className="btn btn-outline-primary"
          >
            Buy More Tickets
          </Link>
        </div>}

        {orders.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-muted">
              You do not have any orders for this event yet.
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {orders.map((order) => {
              const ticket = ticketsById[order.ticketId];
              const isAuction = order.orderType === "auction";

              return (
                <div className="col-12" key={order.id} id={`order-${order.id}`}>
                  <div className="card shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between flex-wrap gap-3">
                        <div>
                          <h2 className="h5 mb-1">
                            {isAuction
                              ? order.auctionItemTitle || "Silent Auction Item"
                              : ticket?.title || order.ticketTitle || "Event Ticket"}
                          </h2>

                          <div className="text-muted small">
                            Order #{order.id}
                          </div>
                        </div>

                        <span
                          className={
                            order.status === "paid"
                              ? "badge bg-success align-self-start"
                              : order.status === "checkout_started"
                              ? "badge bg-warning text-dark align-self-start"
                              : "badge bg-secondary align-self-start"
                          }
                        >
                          {order.status || "unknown"}
                        </span>
                      </div>

                      <hr />

                      <div className="row g-3">
                        <div className="col-6 col-md-3">
                          <div className="fw-bold">Quantity</div>
                          <div>{order.quantity || (isAuction ? 1 : 0)}</div>
                        </div>

                        <div className="col-6 col-md-3">
                          <div className="fw-bold">Amount</div>
                          <div>
                            {formatMoney(
                              order.amountPaid || order.totalAmount
                            )}
                          </div>
                        </div>

                        <div className="col-12 col-md-3">
                          <div className="fw-bold">Created</div>
                          <div>{formatDate(order.createdAt)}</div>
                        </div>

                        <div className="col-12 col-md-3">
                          <div className="fw-bold">Paid</div>
                          <div>{formatDate(order.paidAt)}</div>
                        </div>
                      </div>

                      {order.confirmationEmailSent && (
                        <div className="alert alert-success mt-3 mb-0">
                          Confirmation email sent.
                        </div>
                      )}

                      {isAuction && order.status !== "paid" && order.provider !== "demo" && (order.autoPaymentStatus === "processing" || (order.automaticPaymentRequired && !order.autoPaymentStatus)) && <div className="alert alert-info mt-3">Automatic payment is processing. Refresh to check its status.</div>}
                      {isAuction && order.provider === "demo" && order.status !== "paid" && <div className="alert alert-info mt-3">Demo payment recovery: this sample order does not charge a real card.</div>}
                      {isAuction && order.status !== "paid" && order.autoPaymentStatus !== "processing" && (!order.automaticPaymentRequired || order.autoPaymentStatus === "requires_payment") && order.provider !== "demo" && (
                        <div className="mt-3">
                          <button type="button" disabled={!!payingOrderId} onClick={() => payAuctionOrder(order)}
                            className="btn btn-primary"
                          >
                            {payingOrderId === order.id ? "Opening Payment..." : "Pay Winning Bid"}
                          </button>
                        </div>
                      )}

                      {isAuction && order.auctionItemId && (
                        <div className="mt-3">
                          <Link to={`/${eventslug}/auction/${order.auctionSlug || order.auctionItemId}`}>
                            View Auction Item
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
    </>
  );
};

export default MyOrders;
